/** Typed event bus for resource loading events, one slot per resource type. */
import * as THREE from 'three';
/** The events that change what a cache holds for a key. */
const CHANGE_EVENTS = ['loaded', 'failed', 'invalidated'];
export class ResourceEventBus {
    handlers = new Map();
    threeManager;
    constructor() {
        this.threeManager = new THREE.LoadingManager();
        this.threeManager.onProgress = (url, loaded, total) => {
            this.emit('texture', 'progress', url, { loaded, total });
        };
    }
    /**
     * @param resourceType - Type of resource (texture, material, scene)
     * @param eventType - Type of event (requested, loading, loaded, failed)
     * @param handler - Callback function receiving (id, data)
     */
    on(resourceType, eventType, handler) {
        const key = `${resourceType}:${eventType}`;
        if (!this.handlers.has(key)) {
            this.handlers.set(key, new Set());
        }
        this.handlers.get(key).add(handler);
    }
    off(resourceType, eventType, handler) {
        const key = `${resourceType}:${eventType}`;
        this.handlers.get(key)?.delete(handler);
    }
    /**
     * One subscription to every change on `busTypes`, for a reader of the caches
     * that is not a `useResource` consumer. Returns the unsubscribe.
     */
    onChange(busTypes, listener) {
        const subscriptions = busTypes.flatMap((busType) => CHANGE_EVENTS.map((event) => {
            const handler = (key) => listener({ busType, event, key });
            this.on(busType, event, handler);
            return () => this.off(busType, event, handler);
        }));
        return () => subscriptions.forEach((unsubscribe) => unsubscribe());
    }
    emit(resourceType, eventType, id, data) {
        const key = `${resourceType}:${eventType}`;
        const handlers = this.handlers.get(key);
        if (handlers) {
            for (const handler of handlers) {
                try {
                    handler(id, data);
                }
                catch (error) {
                    console.error(`[ResourceEventBus] Handler error for ${key}:`, error);
                }
            }
        }
    }
    /**
     * Resolve when `eventType` fires for `id`. Reject on `failed`, or on `invalidated`:
     * a full cache clear drops the awaited flight with no loaded or failed emit, and the
     * promise would hang.
     */
    once(resourceType, eventType, id, timeoutMs) {
        return new Promise((resolve, reject) => {
            let timeoutId;
            let resolved = false;
            const cleanup = () => {
                if (resolved)
                    return;
                resolved = true;
                if (timeoutId)
                    clearTimeout(timeoutId);
                this.off(resourceType, eventType, loadedHandler);
                if (eventType !== 'failed') {
                    this.off(resourceType, 'failed', failedHandler);
                }
                if (eventType !== 'invalidated') {
                    this.off(resourceType, 'invalidated', invalidatedHandler);
                }
            };
            const loadedHandler = (eventId, data) => {
                if (eventId === id) {
                    cleanup();
                    resolve(data);
                }
            };
            const failedHandler = (eventId, error) => {
                if (eventId === id) {
                    cleanup();
                    reject(error || new Error(`Resource ${resourceType}:${id} failed to load`));
                }
            };
            const invalidatedHandler = (eventId) => {
                if (eventId === id) {
                    cleanup();
                    reject(new Error(`Resource ${resourceType}:${id} invalidated while awaited`));
                }
            };
            this.on(resourceType, eventType, loadedHandler);
            // Also listen for failed and invalidated, unless that is the awaited event.
            if (eventType !== 'failed') {
                this.on(resourceType, 'failed', failedHandler);
            }
            if (eventType !== 'invalidated') {
                this.on(resourceType, 'invalidated', invalidatedHandler);
            }
            if (timeoutMs && timeoutMs > 0) {
                timeoutId = setTimeout(() => {
                    cleanup();
                    reject(new Error(`Timeout waiting for ${resourceType}:${eventType}:${id}`));
                }, timeoutMs);
            }
        });
    }
    /**
     * Wait for any of the specified IDs to complete (loaded or failed).
     * Returns when all specified IDs have resolved.
     */
    async waitForAll(resourceType, ids, timeoutMs) {
        const results = new Map();
        await Promise.all(ids.map(async (id) => {
            try {
                const data = await this.once(resourceType, 'loaded', id, timeoutMs);
                results.set(id, data);
            }
            catch {
                results.set(id, null);
            }
        }));
        return results;
    }
    getThreeManager() {
        return this.threeManager;
    }
    /** For memory-leak tests. */
    getHandlerCount(resourceType, eventType) {
        const key = `${resourceType}:${eventType}`;
        return this.handlers.get(key)?.size || 0;
    }
    /** For memory-leak tests. */
    getTotalHandlerCount() {
        let total = 0;
        for (const handlers of this.handlers.values()) {
            total += handlers.size;
        }
        return total;
    }
    /** For cleanup and tests. */
    clear() {
        this.handlers.clear();
    }
}
//# sourceMappingURL=ResourceEventBus.js.map